import { takesMoney } from "../utils/takesMoney";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { usersApi } from "../services/users";
import { selectPath, trackOnce } from "../lib/analytics";
import "./RequestPage.css";
import "./RequestPageBackdrop.css";
import { roundPriceStep } from "../utils/roundPriceStep";
import { defaultPriceFor } from "../utils/payinConfig";
import FanShopSheet, { ShopChoice } from "../components/FanShopSheet";
import { useScrollLockHeal } from "../hooks/useScrollLockHeal";
import { absMediaUrl } from "../utils/mediaUrl";
import "../components/ThreeStepIndicator.css";
import { ensureFanSession, getFanUser, FAN_ACCOUNT_EVENT } from "../lib/fanAccount";
import { usePrevOrders } from "../components/PrevOrderChips";
import PrevOrderChips from "../components/PrevOrderChips";
import FanPageHeader from "../components/FanPageHeader";
import TipCheckout from "../components/TipCheckout";
import BuyCheckout, { PlaylistBanner } from "../components/BuyCheckout";
import SongSearchFeed from "../components/SongSearchFeed";
import SongCheckout from "../components/SongCheckout";
import OfflineBlockModal from "../components/OfflineBlockModal";
import { useTipCheckout } from "../hooks/useTipCheckout";
import { useBuyCheckout } from "../hooks/useBuyCheckout";
import { useSongRequest } from "../hooks/useSongRequest";
import { publicUrl } from "../utils/publicUrl";
import { t } from "../i18n";

// parseGuestIdentity moved to lib/fanProfile (Phase 2) — the profile sheet's
// name editor shares the exact same "rockstar123 @insta" parsing.

// Loading the talent: only a real 404 means "not on REQUEST". A Wi-Fi blip,
// a 5xx or a 429 from the per-IP limit (one venue = one IP) is retried
// quietly on this backoff, then the fan gets Try again — never the
// "hasn't joined" dead end after scanning a QR at a party (2026-10-10).
const DJ_RETRY_DELAYS_MS = [1000, 2000, 4000];

/** Draft profile fields merged over API `/users/:slug` when embedded in Profile → Preview. */
export type ProfilePreviewDraft = {
  name: string;
  bio: string;
  instagram: string;
  /** Resolved URL for avatar (blob or absolute). */
  avatarDisplayUrl: string | null;
  currency: "usd" | "eur" | "cad" | "gbp" | "aud" | "nzd" | "sgd" | "hkd" | "jpy" | "mxn" | "brl" | "chf" | "sek" | "dkk" | "nok" | "pln" | "czk" | "huf" | "ron";
  pricePerRequest?: number;
  venueName?: string;
  showFastPass?: boolean;
  askForNickname?: boolean;
  allowPersonalizedMessages?: boolean;
};

export type RequestPageProps = {
  profilePreviewDraft?: ProfilePreviewDraft | null;
  /** When previewing from Profile, the logged-in user's slug (route may be /profile). */
  profilePreviewSlug?: string | null;
};

const RequestPage: React.FC<RequestPageProps> = ({
  profilePreviewDraft = null,
  profilePreviewSlug = null,
}) => {
  const { slug: slugFromRoute } = useParams<{ slug: string }>();

  // Growth funnel: a DJ's page is the fan landing (QR scans land here).
  useEffect(() => {
    selectPath("fan", "dj_page");
    trackOnce("landing_viewed", "dj_page", { screen: "dj_page" });
  }, []);
  const slug = profilePreviewSlug || slugFromRoute;
  const isProfilePreview = Boolean(profilePreviewDraft);
  const [dj, setDj] = useState<any>(null);
  const [showOfflineBlock, setShowOfflineBlock] = useState(false);
  const [comment, setComment] = useState("");
  const [commentEnabled, setCommentEnabled] = useState(false);
  const [emailForLink, setEmailForLink] = useState("");

  // #9 growth loop — Share pill next to Tip Me. Native share sheet on
  // phones; desktop falls back to copy + a 2s "Copied ✓" flash.
  const [shareCopied, setShareCopied] = useState(false);
  const handleShare = async () => {
    const url = publicUrl(`/dj/${slug}/request`);
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ url });
        return;
      } catch {
        return; // user closed the sheet — not an error
      }
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Locked-down/older browsers: legacy copy path.
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      let ok = false;
      try {
        ok = document.execCommand("copy");
      } catch {
        ok = false;
      }
      document.body.removeChild(ta);
      if (!ok) return;
    }
    setShareCopied(true);
    window.setTimeout(() => setShareCopied(false), 2000);
  };

  // Guest identity — read/write helpers live in lib/fanProfile (Phase 2) so
  // the header chip and profile sheet share the exact same storage rules
  // (localStorage-first with a one-time sessionStorage fallback migration).
  // Identity rework (founder decision): the name on a request comes from the fan
  // ACCOUNT — signed-out fans request anonymously, and the old nickname
  // modal at QR-scan is gone. Signing up on the confirmation page claims the
  // request and names it retroactively (server-side propagation).
  const [clientNickname, setClientNickname] = useState<string>(
    () => getFanUser()?.name || ""
  );
  const [clientInstagram, setClientInstagram] = useState<string>(
    () => getFanUser()?.instagram || ""
  );

  // Keep the form's copy in sync with the account (sign-in mid-visit, name
  // edits in the profile sheet) so the NEXT order carries the fresh name.
  useEffect(() => {
    const sync = () => {
      const f = getFanUser();
      setClientNickname(f?.name || "");
      setClientInstagram(f?.instagram || "");
    };
    ensureFanSession().then(sync).catch(() => {});
    window.addEventListener(FAN_ACCOUNT_EVENT, sync);
    return () => window.removeEventListener(FAN_ACCOUNT_EVENT, sync);
  }, []);

  const [notFound, setNotFound] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadTry, setLoadTry] = useState(0);

  const viewDj = useMemo(() => {
    if (!dj) return null;
    if (!profilePreviewDraft) return dj;
    const insta = (profilePreviewDraft.instagram || "").replace(/^@+/, "");
    return {
      ...dj,
      name: profilePreviewDraft.name,
      bio: profilePreviewDraft.bio || "",
      instagram: insta || undefined,
      avatarUrl: profilePreviewDraft.avatarDisplayUrl ?? dj.avatarUrl,
      currency: profilePreviewDraft.currency ?? dj.currency,
      pricePerRequest:
        profilePreviewDraft.pricePerRequest !== undefined
          ? profilePreviewDraft.pricePerRequest
          : dj.pricePerRequest,
      venueName:
        profilePreviewDraft.venueName !== undefined
          ? profilePreviewDraft.venueName
          : dj.venueName,
      showFastPass:
        profilePreviewDraft.showFastPass !== undefined
          ? profilePreviewDraft.showFastPass
          : dj.showFastPass,
      askForNickname:
        profilePreviewDraft.askForNickname !== undefined
          ? profilePreviewDraft.askForNickname
          : dj.askForNickname,
      allowPersonalizedMessages:
        profilePreviewDraft.allowPersonalizedMessages !== undefined
          ? profilePreviewDraft.allowPersonalizedMessages
          : dj.allowPersonalizedMessages,
      isAcceptingRequests: true,
    };
  }, [dj, profilePreviewDraft]);

  const tip = useTipCheckout({ dj, viewDj, isProfilePreview, comment, clientNickname, clientInstagram });
  const { tipMode, setTipAmount } = tip;

  // Phase C: "5" is a dollar-class default. Once the DJ (and their currency)
  // loads, upgrade a still-pristine field to that currency's default (50 kr,
  // 2000 Ft, …). One-shot — never fights a fan-typed value.
  const tipDefaultAppliedRef = useRef(false);
  useEffect(() => {
    if (tipDefaultAppliedRef.current || !viewDj?.currency) return;
    tipDefaultAppliedRef.current = true;
    setTipAmount((cur) => (cur === "5" ? String(defaultPriceFor(viewDj.currency)) : cur));
  }, [viewDj?.currency, setTipAmount]);

  const prev = usePrevOrders({ isProfilePreview });

  /** === DJ === */
  useEffect(() => {
    if (!slug) return;
    setLoadFailed(false);
    let cancelled = false;
    let timer: number | undefined;
    const attempt = (n: number) => {
      usersApi.get(slug)
        .then((dj) => {
          if (!cancelled) setDj(dj);
        })
        .catch((err) => {
          if (cancelled) return;
          if (err?.response?.status === 404) {
            setNotFound(true);
          } else if (n < DJ_RETRY_DELAYS_MS.length) {
            timer = window.setTimeout(() => attempt(n + 1), DJ_RETRY_DELAYS_MS[n]);
          } else {
            console.error("Failed to load DJ", err);
            setLoadFailed(true);
          }
        });
    };
    attempt(0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [slug, loadTry]);

  /** Цены/Fees */
  // FREE TIER: a talent who can't take money yet is free, whatever price they
  // have saved. Fans already get 0 from the server; this makes the talent's
  // own "Preview my public page" show the page fans really see.
  const minPrice = takesMoney(viewDj) && Number.isFinite(Number(viewDj?.pricePerRequest))
    ? Number(viewDj!.pricePerRequest)
    : 0;
  const isFree = minPrice === 0;

  // базовые значения, которые показываем в шапке
  const djCur = viewDj?.currency;
  const djMinBase = roundPriceStep(minPrice || 0, djCur);
  const fastPassBase = roundPriceStep((minPrice || 0) * 2, djCur);

  const song = useSongRequest({ dj, viewDj, isProfilePreview, minPrice, isFree, djCur, comment, clientNickname, clientInstagram });

  /** Land pinned to the very top on every entry path, and silently heal a
      leaked body scroll-lock — including the iOS back-forward-cache restore
      the old inline heal missed (build 34: shared hook, also on the
      confirmation pages). */
  useScrollLockHeal(!isProfilePreview);

  const buy = useBuyCheckout({ dj, viewDj, isProfilePreview, clientNickname, clientInstagram, tip });
  const { buyMode, shopSheetOpen, setShopSheetOpen, setlistOn, setlistChipPrice, shopPlaylists, openBuyMode } = buy;

  if (notFound) {
    return (
      <div className="rq-container">
        <div className="rq-card" style={{ textAlign: "center", padding: "60px 24px" }}>
          <p style={{ fontSize: "1.2rem", fontWeight: 600, marginBottom: "12px" }}>
            {t("requestPage.thisPerformerHasnT")}
          </p>
          <Link to="/" style={{ color: "#24C961", textDecoration: "underline" }}>
            {t("requestPage.backToHome")}
          </Link>
        </div>
      </div>
    );
  }

  if (loadFailed) {
    // Recovery is the fan's tap away — same words as the venue page.
    return (
      <div className="rq-container">
        <div className="rq-card" style={{ textAlign: "center", padding: "60px 24px" }}>
          <p style={{ fontSize: "1.2rem", fontWeight: 600, marginBottom: "12px" }}>
            {t("establishmentPage.couldnTLoadThis")}
          </p>
          <p>{t("establishmentPage.checkYourConnectionAnd")}</p>
          <button type="button" className="rq-cta on" onClick={() => setLoadTry((n) => n + 1)}>
            {t("establishmentPage.tryAgain")}
          </button>
        </div>
      </div>
    );
  }

  if (!dj) {
    return (
      <div className="rq-container">
        <div className="rq-card">
          <p style={{ textAlign: "center" }}>{t("requestPage.loading")}</p>
        </div>
      </div>
    );
  }

  const displayDj = viewDj!;

  // Shared helper (utils/mediaUrl) — SuccessPage skipped this prefix and
  // shipped broken ribbon photos; one definition now.
  const avatarImgSrc = (u: string) => absMediaUrl(u) as string;

  return (
    <div className="rq-container">
      {!isProfilePreview && <PrevOrderChips prev={prev} />}
      <div className="rq-card">
        <FanPageHeader
          displayDj={displayDj}
          viewDj={viewDj}
          isProfilePreview={isProfilePreview}
          shareCopied={shareCopied}
          handleShare={handleShare}
          avatarImgSrc={avatarImgSrc}
          tip={tip}
          buy={buy}
          song={song}
        />

        <TipCheckout displayDj={displayDj} isProfilePreview={isProfilePreview} emailForLink={emailForLink} setEmailForLink={setEmailForLink} tip={tip} />

        <BuyCheckout
          displayDj={displayDj}
          isProfilePreview={isProfilePreview}
          avatarImgSrc={avatarImgSrc}
          emailForLink={emailForLink}
          setEmailForLink={setEmailForLink}
          buy={buy}
        />

        <PlaylistBanner displayDj={displayDj} isProfilePreview={isProfilePreview} buy={buy} tipMode={tipMode} song={song} />

        {/* The shelf itself (founder pick 5A inside 5C's sheet). */}
        <FanShopSheet
          open={shopSheetOpen && !isProfilePreview}
          onClose={() => setShopSheetOpen(false)}
          djName={displayDj.name || "this artist"}
          currency={displayDj.currency}
          livePrice={setlistOn ? setlistChipPrice : null}
          playlists={shopPlaylists}
          onPick={(c: ShopChoice) => openBuyMode(c.kind === "shop" ? c.playlist.id : null)}
        />
        <SongSearchFeed
          displayDj={displayDj}
          isProfilePreview={isProfilePreview}
          isFree={isFree}
          minPrice={minPrice}
          djMinBase={djMinBase}
          tipMode={tipMode}
          buyMode={buyMode}
          setShowOfflineBlock={setShowOfflineBlock}
          song={song}
          message={
            displayDj.allowPersonalizedMessages !== false
              ? { enabled: commentEnabled, toggle: () => setCommentEnabled((v) => !v) }
              : null
          }
        />

        <SongCheckout
          dj={dj}
          displayDj={displayDj}
          isProfilePreview={isProfilePreview}
          isFree={isFree}
          minPrice={minPrice}
          djMinBase={djMinBase}
          fastPassBase={fastPassBase}
          tipMode={tipMode}
          buyMode={buyMode}
          comment={comment}
          setComment={setComment}
          commentEnabled={commentEnabled}
          setCommentEnabled={setCommentEnabled}
          emailForLink={emailForLink}
          setEmailForLink={setEmailForLink}
          clientNickname={clientNickname}
          clientInstagram={clientInstagram}
          song={song}
        />

        {/* The tips fine print moved inside the tip card (facelift). */}
      </div>

      {/* The nickname-at-QR-scan modal is GONE (identity rework, founder
          8/26): fans request anonymously; names come from accounts. */}
      <OfflineBlockModal showOfflineBlock={showOfflineBlock} setShowOfflineBlock={setShowOfflineBlock} displayDj={displayDj} tip={tip} />
    </div>
  );
};

export default RequestPage;
